import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';

import { apiDriverAdministration } from '#store/apiDriverAdministration.ts';
import { apiOrderCommunicationBooking } from '#store/apiOrderCommunicationBooking.ts';
import { apiTourService } from '#store/apiTourService.ts';

import { config } from '../../config';
import appReducer from '../../data/appSlice';
import widgetReducer from '../../store/widgetSlice';
import langReducer from '../lang/langSlice';
import loginReducer from '../login/loginSlice';
import tokenReducer from '../tokenHandling/tokenSlice';
import configReducer from './configSlice';

// Create the root reducer independently to obtain the RootState type
const rootReducer = combineReducers({
    config: configReducer,
    lang: langReducer,
    app: appReducer,
    widget: widgetReducer,
    login: loginReducer,
    tokenHandling: tokenReducer,
    [apiTourService.reducerPath]: apiTourService.reducer,
    [apiDriverAdministration.reducerPath]: apiDriverAdministration.reducer,
    [apiOrderCommunicationBooking.reducerPath]: apiOrderCommunicationBooking.reducer,
});

export const getStore = (preloadedState?: Partial<RootState>) =>
    configureStore({
        reducer: rootReducer,
        preloadedState: {
            widget: config.enableMockServer
                ? {
                      selectedAssetId: config.mockSelectedAssetId,
                      visibleWidgets: ['this'],
                      widgetId: 'this',
                  }
                : undefined,
            ...preloadedState,
        },

        // Adding the api middleware enables caching, invalidation, polling,
        // and other useful features of `rtk-query`.
        middleware: getDefaultMiddleware =>
            getDefaultMiddleware()
                .concat(apiTourService.middleware)
                .concat(apiDriverAdministration.middleware)
                .concat(apiOrderCommunicationBooking.middleware),
    });

export const store = getStore();

// Infer the store, state and dispatch types from the store instance
export type RootState = ReturnType<typeof rootReducer>;
export type RootStore = ReturnType<typeof getStore>;
export type AppDispatch = RootStore['dispatch'];

// optional, but required for refetchOnFocus/refetchOnReconnect behaviors
// see `setupListeners` docs - takes an optional callback as the 2nd arg for customization
setupListeners(store.dispatch);
